import React, { useState, useRef, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import * as api from "../../api/index";
import { getBankRecData } from "../../api/accountsAPI";
import { changeLedgerLink } from "actions/ledgerLinks.action";
import { Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { Grid, GridColumn as Column } from "@progress/kendo-react-grid";
import {
  AmountFormat,
  DateFormat1,
  DateFormat,
  AmountFormatAbs,
} from "components/common/formatter";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import { Icon } from "@progress/kendo-react-common";
import { InputClearValue, TextBox } from "@progress/kendo-react-inputs";
import { process, filterBy } from "@progress/kendo-data-query";
import { toast } from "react-toastify";
import { Button } from "@progress/kendo-react-buttons";
import InfoBox from "components/Global/InfoBox";
import { NominalTypeFormat } from "components/common/nominalTypeFormatter";
import { BankRecHierarchyCell } from "./bankRecGridHelpers";
import { useBankRecExpandedGrid } from "./useBankRecExpandedGrid";
import { useActiveBanks } from "services/nominalsService";

const DATA_ITEM_KEY = "postingId";
const SELECTED_FIELD = "selected";

export default function BankRec() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: activeBanks } = useActiveBanks();
  const [nominalFilterData, setNominalFilterData] = useState([]);
  const [nominalValue, setNominalvalue] = useState(null);
  const [reconcile, setReconcile] = useState(new Date());
  const [StatementBalance, setStatementBalance] = useState(0.0);
  const [lastStatementBalance, setLastStatementBalance] = useState(0.0);
  const [difference, setDifference] = useState(0.0);
  const [gridData, setGridData] = useState([]);
  const [filterGridData, setFilterGridData] = useState([]);
  const [header, setHeader] = useState("");
  const [dataState, setDataState] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedState, setSelectedState] = useState({});
  const [dataResult, setDataResult] = useState(
    process(
      gridData?.map((dataItem) => Object.assign({ selected: false }, dataItem)),
      {},
    ),
  );
  const [filterValue, setFilterValue] = useState("");
  const [filterArray, setFilterArray] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const gridRef = useRef(null);

  const filterNominalData = (filter) => {
    if (!activeBanks?.length) return [];
    return filterBy(activeBanks, filter);
  };

  const fetchBankRec = async (nominalId, reconcile) => {
    const toDate = DateFormat1(reconcile);
    try {
      const res = await getBankRecData(nominalId, toDate);
      setLastStatementBalance(res.data.data.header.lastRecStatement);
      setStatementBalance(res.data.data.statementBalance);
      const result = [...res.data.data.items];
      setHeader(res.data.data.header);
      const updatedResult = result?.map((item) => {
        return { ...item, add: false, inEdit: true };
      });
      setGridData(updatedResult);
      setFilterGridData(updatedResult);
      setDataResult({
        ...process(
          updatedResult?.map((dataItem) =>
            Object.assign({ selected: false }, dataItem),
          ),
          {},
        ),
      });

      const saveItem = result
        .map((item) => {
          if (item.saved) {
            return item;
          }
        })
        .filter((item) => item !== undefined)
        .map((item) => {
          const obj = { [item.postingId]: item.saved };
          return obj;
        });

      let outputObj = {};
      let recursive = (obj, name) => {
        for (let key in obj) {
          if (typeof obj[key] == "object") {
            recursive(obj[key], +key);
          } else {
            outputObj[key] = obj[key];
          }
        }
      };
      recursive(saveItem, "");
      setSelectedState(outputObj);
    } catch (err) {}
  };

  useEffect(() => {
    if (nominalValue?.nominalRef) {
      fetchBankRec(nominalValue?.nominalRef, reconcile);
    }
  }, [nominalValue, reconcile]);

  const filterNominal = (event) => {
    setNominalFilterData(filterNominalData(event.filter));
  };

  useEffect(() => {
    setNominalFilterData(activeBanks ?? []);
  }, [activeBanks]);

  useEffect(() => {
    getSelectedAmount();
  }, []);

  const onSearchValueChange = (ev) => {
    let value = ev.value;
    const data = value.split(",");
    setFilterValue(value);
    setFilterArray(data);
    // setFilterArray(data);
    //// To show only filter data uncommented below lines
    //let newData = gridData.filter((item) => {
    //    let match = false;
    //    for (const property in item) {
    //        if (
    //            item[property]
    //                ?.toString()
    //                ?.toLocaleLowerCase()
    //                .indexOf(value?.toLocaleLowerCase()) >= 0
    //        ) {
    //            match = true;
    //        }

    //        if (
    //            item[property]?.toLocaleDateString &&
    //            item[property]?.toLocaleDateString().indexOf(value) >= 0
    //        ) {
    //            match = true;
    //        }
    //    }
    //    return match;
    //});
    //setFilterGridData(newData);
    //let clearedPagerDataState = { ...dataState, take: 10, skip: 0 };
    //let processedData = process(newData, clearedPagerDataState);
    //setDataResult({ ...processedData });
    //setDataState(clearedPagerDataState);

    // Scroll to the first found location
    if (gridRef.current) {
      const firstMatchIndex = dataResult.data.findIndex((item) =>
        data.some((filter) =>
          Object.values(item).some((val) =>
            val?.toString().toLowerCase().includes(filter.toLowerCase()),
          ),
        ),
      );
      if (firstMatchIndex >= 0) {
        gridRef.current.scrollIntoView({ rowIndex: firstMatchIndex });
      }
    }
  };

  const dataStateChange = (event) => {
    setDataResult({ ...process(filterGridData, event.dataState) });
    setDataState(event.dataState);
  };

  const onSelectionChange = React.useCallback(() => {}, []);

  const getHighlight = (valueUsed, filter, filteredValue) => {
    let value = valueUsed;
    let index = value.toLocaleLowerCase().indexOf(filteredValue.toLocaleLowerCase());

    if (index >= 0) {
      let left = value.substr(0, index);
      let right = value.substring(index + filter.length, value.length);
      return (
        <React.Fragment>
          {left}
          <span className="shadow-lg" style={{ backgroundColor: "#FFC000" }}>
            {value?.substr(index, filter.length)}
          </span>
          {right}
        </React.Fragment>
      );
    }
  };

  const cellRender = React.useCallback(
    (td, props) => {
      let value = td.props.children;
      // for (let items in filterArray) {
      if (props.field === "netAmount") {
        value = td.props.children.replace(",", "");
      }

      for (let items of filterArray) {
        if (
          items &&
          items.length > 0 &&
          value?.substr &&
          value?.toLocaleLowerCase().indexOf(items?.toLocaleLowerCase()) >= 0
        ) {
          const children = getHighlight(value, items?.toLocaleLowerCase(), items);
          return React.cloneElement(td, [props], [children]);
        }
      }
      return td;
    },
    [filterArray],
  );

  const handleMatterLedgerOpen = useCallback(
    (dataItem) => {
      const matterId = dataItem?.matterID;
      if (!matterId) return;
      dispatch(changeLedgerLink("MatterLedger", { ...dataItem, matterId }));
      navigate("/accounts/matterledger");
    },
    [dispatch, navigate],
  );

  const MatterRefCell = (props) => {
    if (props.dataItem?.isGroupParent) {
      const bankRef = props.dataItem.reference ?? "";
      return cellRender(<td>{bankRef}</td>, props);
    }
    const refText = props.dataItem.matterRef ?? "";
    const matterId = props.dataItem.matterID;
    const inner = matterId ? (
      <span
        style={{ cursor: "pointer" }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleMatterLedgerOpen(props.dataItem);
          }
        }}
        onClick={() => handleMatterLedgerOpen(props.dataItem)}>
        <u>{refText}</u>
      </span>
    ) : (
      refText
    );
    return cellRender(<td>{inner}</td>, props);
  };

  const ReferenceCell = (props) => {
    if (props.dataItem?.isGroupParent) {
      return cellRender(<td></td>, props);
    }
    const v = props.dataItem?.reference ?? "";
    return cellRender(<td>{v}</td>, props);
  };

  const PaymentCell = (props) => {
    const dateField = props.dataItem[props.field];
    if (dateField <= 0) {
      return cellRender(
        <td style={{ textAlign: "right" }}>{AmountFormatAbs(dateField)}</td>,
        props,
      );
    } else {
      return <td></td>;
    }
  };

  const RecieptCell = (props) => {
    const dateField = props.dataItem[props.field];
    if (dateField > 0) {
      return cellRender(
        <td style={{ textAlign: "right" }}>{AmountFormat(dateField)}</td>,
        props,
      );
    } else {
      return <td></td>;
    }
  };

  const DateCell = (props) => {
    const dateField = props.dataItem[props.field];
    if (dateField) {
      return cellRender(<td>{DateFormat(dateField)}</td>, props);
    } else {
      return <td></td>;
    }
  };

  function sumArray(array) {
    if (Array.isArray(array)) {
      let sum = 0;

      for (const item of array) {
        sum += item;
      }
      return Math.round(sum * 100) / 100;
    }
  }

  const getSelectedAmount = () => {
    const selected = filterGridData?.map((item) => {
      if (
        selectedState?.hasOwnProperty(item.postingId) &&
        selectedState?.[item?.postingId]
      ) {
        return item.netAmount;
      } else {
        return 0;
      }
    });

    const unSelected = filterGridData?.map((item) => {
      if (
        selectedState?.hasOwnProperty(item.postingId) &&
        selectedState?.[item.postingId]
      ) {
        return 0;
      } else {
        return item?.netAmount;
      }
    });
    const sumSel = sumArray(selected);
    const sumunSel = sumArray(unSelected);

    return { sumSel, sumunSel };
  };

  const dataReturner = () => {
    let unSelected = filterGridData?.map((item) => {
      if (
        selectedState.hasOwnProperty(item.postingId) &&
        selectedState[item.postingId]
      ) {
        return item.postingId;
      } else {
        return 0;
      }
    });

    let selected = filterGridData?.map((item) => {
      if (
        selectedState.hasOwnProperty(item.postingId) &&
        selectedState[item.postingId]
      ) {
        return 0;
      } else {
        return item.postingId;
      }
    });

    let filterSelected = selected.filter((item) => item !== 0);
    let filterunSelected = unSelected.filter((item) => item !== 0);

    const data = {
      data: {
        toDate: reconcile,
        statementBalance: StatementBalance,
        selectedItems: [...filterunSelected],
        unselectedItems: [...filterSelected],
      },
    };
    return data;
  };

  const checkDifferenceAmount = () => {
    const diffAmount =
      Math.round(
        (StatementBalance - (lastStatementBalance + getSelectedAmount().sumSel)) *
          100,
      ) / 100;
    if (isNaN(diffAmount)) {
      setDifference(0);
      return;
    }
    setDifference(diffAmount);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const data = dataReturner();
      await api.addBankRecs(nominalValue?.nominalRef, data);
      setLoading(false);

      toast.success(`Bank Record saved successfully.`);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    try {
      if (difference === 0) {
        const data = dataReturner();
        data.data.balance_RecDate = header.balance_RecDate;
        const res = await api.postBankRecs(nominalValue?.nominalRef, data);
        fetchBankRec(nominalValue?.nominalRef, reconcile);
          if (res) {
              const bankrecId = res.data.data.bankRecId;
              return toast.success(`Bank Rec Posted. Bank Rec Id: ${bankrecId}`);
          }
        
      } else {
        toast.error("Difference value should be '0'");
      }
    } catch (error) {}
  };
  const reset = () => {
    setNominalvalue(null);
    setHeader("");
    setStatementBalance(0);
    setLastStatementBalance(0);
    setDifference(0);
    setGridData([]);
    setFilterGridData([]);
    setDataResult(process([], {}));
    setSelectedState({});
    resetSearch();
  };

  useEffect(() => {
    checkDifferenceAmount();
  }, [StatementBalance, getSelectedAmount]);

  const BooleanHeaderCell = (props) => (
    <span
      className="d-flex pb-2 "
      style={{ color: "#000000", fontSize: "15px", fontWeight: "700" }}>
      Select
    </span>
  );

  const resetSearch = () => {
    setFilterArray([]);
    setFilterValue("");
  };

  const { displayedGridData, SelectCell, rowRender, DetailComponent } =
    useBankRecExpandedGrid({
      dataResultData: dataResult?.data,
      expandedGroups,
      selectedState,
      setSelectedState,
      selectField: SELECTED_FIELD,
      DateCell,
      MatterRefCell,
      RecieptCell,
      PaymentCell,
    });

  return (
    <div className="page h-100 d-flex flex-column">
      <div className="d-flex">
        <div className="mb-xxl-0 mb-3">
          <div className="d-flex align-items-start">
            <Label className="me-4 label mt-2 pt-1">Bank</Label>
            <div>
              <ComboBox
                style={{ width: "300px" }}
                data={nominalFilterData}
                textField={"display"}
                dataItemKey="nominalRef"
                filterable={true}
                onFilterChange={filterNominal}
                value={nominalValue}
                onChange={(e) => {
                  if (e.value === null) {
                    reset();
                    return;
                  }
                  setNominalvalue(e.value);
                }}
                placeholder={"Search Nominals"}
              />
              <InfoBox
                className="mt-2 w-100"
                text={
                  nominalValue !== null &&
                  `${NominalTypeFormat(
                    nominalValue?.type,
                  )} - ${NominalTypeFormat(nominalValue?.subType)}`
                }
                title="Type"
              />
            </div>
            <Button
              className="common-btn ms-3"
              svgIcon={refreshIcon}
              disabled={nominalValue === null}
              onClick={() => {
                fetchBankRec(nominalValue?.nominalRef, reconcile);
                resetSearch();
              }}>
              Refresh
            </Button>
          </div>
        </div>

        <div className=" ms-auto justify-content-end">
          <div
            className="ms-xxl-auto"
            style={{
              width: "500px",
              height: "150px",
              background: "#FFFFFF",
              boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "10px",
            }}>
            <div className="container">
              <div className="row pt-3 px-3 ">
                <div className="col-6 pe-5">
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Balance</p>
                    <p className="bt-value text-end">
                      {AmountFormat(header.balance || 0)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Bal @Rec Date</p>
                    <p className="bt-value text-end">
                      {AmountFormat(header.balance_RecDate || 0)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Payments</p>
                    <p className="bt-value text-end">
                      {AmountFormat(header.payments || 0)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Receipts</p>
                    <p className="bt-value text-end">
                      {AmountFormat(header.receipts || 0)}
                    </p>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Selected</p>
                    <p className="bt-value text-end">
                      {AmountFormat(getSelectedAmount().sumSel)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Unselected</p>
                    <p className="bt-value text-end">
                      {AmountFormat(getSelectedAmount().sumunSel)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Last Rec Statement</p>
                    <p className="bt-value text-end">
                      {AmountFormat(header.lastRecStatement || 0)}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="bt-text">Last Rec by</p>
                    <p className="bt-value text-end">{header.lastRecBy}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2">
        <div className="d-flex align-items-end justify-content-between">
          <div className="align-items-xxl-end d-flex flex-xxl-row flex-column  me-auto">
            <div className="me-4" style={{ width: "246px", maxWidth: "246px" }}>
              <Label className="mb-1">Reconcile to</Label>
              <DatePicker
                className="datepicker"
                toggleButton={CalenderCustomToggleButton}
                value={reconcile}
                onChange={({ value }) => setReconcile(value)}
              />
            </div>
            <div className="me-4" style={{ width: "246px", maxWidth: "246px" }}>
              <Label className="d-block mb-1">Search</Label>
              <TextBox
                value={filterValue}
                onChange={onSearchValueChange}
                placeholder={"Search"}
                suffix={() => (
                  <React.Fragment>
                    {filterValue !== "" && (
                      <InputClearValue onClick={resetSearch}>
                        <Icon name="x" />
                      </InputClearValue>
                    )}
                  </React.Fragment>
                )}
              />
            </div>
          </div>
          <div className="d-flex flex-xxl-row flex-column">
            <div
              className="ms-auto right-align "
              style={{ width: "246px", maxWidth: "246px" }}>
              <Label className="mb-1">Statement Balance</Label>
              <NumericTextBox
                id="netAmount"
                onChange={(e) => setStatementBalance(e.value)}
                value={StatementBalance}
                spinners={false}
                format="n2"
                placeholder="0.00"
              />
            </div>

            <div
              className="ms-4 right-align"
              style={{ width: "246px", maxWidth: "246px" }}>
              <Label className="mb-1">Difference</Label>
              <NumericTextBox
                id="netAmount"
                value={difference}
                spinners={false}
                format="n2"
                placeholder="0.00"
                readOnly={true}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 flex-grow-1 overflow-auto bankRec">
        <Grid
          ref={gridRef}
          sortable={true}
          cellRender={cellRender}
          rowRender={rowRender}
          cells={{ hierarchy: { data: BankRecHierarchyCell } }}
          detail={DetailComponent}
          expandField="expanded"
          onExpandChange={(e) => {
            if (!e.dataItem?.isGroupParent) return;
            setExpandedGroups((prev) => ({
              ...prev,
              [e.dataItem.groupKey]: e.value,
            }));
          }}
          data={displayedGridData?.map((item) => ({
            ...item,
            [SELECTED_FIELD]: item.isGroupParent
              ? item.groupAllSelected
              : !!selectedState[item.postingId],
          }))}
          {...dataState}
          onDataStateChange={dataStateChange}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          editField="inEdit"
          className={`pb-1 grid-row-select h-100 overflow-auto grid-minwidth-col6-150`}>
          <Column
            field="date"
            title="Date"
            editable={false}
            cell={DateCell}
            width="110px"
          />
          <Column field="postingType" title="Type" editable={false} width="80px" />
          <Column
            field="matterRef"
            title="Matter Ref"
            editable={false}
            width="120px"
            cell={MatterRefCell}
          />
          <Column
            field="reference"
            title="Ref"
            editable={false}
            width="120px"
            className="text-wrap bank-rec-ref-min"
            cell={ReferenceCell}
          />
          <Column field="details" title="Details" editable={false} />

          <Column field="payee" title="Payee(r)" editable={false} width="140px" className="fs-sm"/>

          <Column
            field="transactionPeriod"
            title="Period"
            editable={false}
            width="80px"
          />
          <Column
            field="netAmount"
            title="Receipt"
            cell={RecieptCell}
            editable={false}
            width="120px"
          />
          <Column
            field="netAmount"
            title="Payment"
            cell={PaymentCell}
            editable={false}
            width="120px"
          />
          {/*<Column field="saved" title="Doc" editable={false} width="80px" />*/}
          <Column
            field={SELECTED_FIELD}
            headerCell={BooleanHeaderCell}
            title="Select"
            editor="boolean"
            cell={SelectCell}
            width="80px"
          />
        </Grid>
      </div>

      <div className="d-flex justify-content-end mt-2 pb-2">
        <Button className="common-btn bgBtnSecondary" onClick={handleSave}>
          {loading ? "Saving..." : "Save"}
        </Button>
        <Button className="common-btn ms-3" onClick={handlePost}>
          {"Post"}
        </Button>
      </div>
    </div>
  );
}
